/* eslint-disable max-len */
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { extend, QueryContext, useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Sticky } from '@thd-olt-component-react/sticky';
import { ProductResultsSearchMethodsPLP } from '../utils/product-results-search-methods-PLP';
import { ProductResultsContext } from './ProductResultsContext';
import { FilterAndSortPlaceholder } from './FilterAndSort/FilterAndSortPlaceholder';
import { PrimaryFilters } from './PrimaryFilters/PrimaryFilters';
import { publish } from '../publisher';
import { DrawerAndAppliedRefinements as Drawer } from './FilterAndSort/DrawerAndAppliedRefinements';
import { MainFilter } from './FilterAndSort/MainFilter';
import {
  getAppliedDimensions,
  getClearAllWithSort,
  getRefinementUrlWithSort,
  sortAscending,
  sortDimensions,
  sortRefinementKeys,
  sortSelectedDimensions,
  findCustomPriceRange,
  getDimensionsForUrl,
  getRefinemntsLabelsForUrl,
  ON_DISPLAY_ITEM,
  filtersCatStyleCheck,
  sortOrder,
  onMultiStoreSelectionChange,
  createDimensionWithRefinements,
  storeLifecycleEvent,
  extractNavParamFromClearAllURL
} from '../product-results-helpers';
import '../styles/filter-and-sort.scss';
import OnDisplayContext from './OnDisplayContext';
import { productResultsDataModel } from '../models/productResultsDataModel';
import { productRefinementCountDataModel } from '../models/refinementCountDataModel';

const {
  func, bool, number, string,
} = PropTypes;

const ResultsFilterAndSortDrawer = ({
  onDimensionsChange: onDimensionsChangeProp,
  onRefinementSelected,
  onSortChange: onSortChangeProp,
  stickyOffset,
  multiStoreSelection,
  enableMultiStore,
  onDisplay,
  onDisplayChange,
  disableStickyFilter,
  isTableView,
  hideViewAll: hideViewAllProp,
  isGetItFastOnPlp
}) => {

  const { channel, customContext, path, store } = useContext(ExperienceContext);
  const { data: wrapperData, queryVariables, pageContext, loading: wrapperLoading } = useContext(ProductResultsContext);
  const [onDisplayClick, setOnDisplayClick] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [primaryFilterDrawerOpen, setPrimaryFilterDrawerOpen] = useState(false);
  const clearAllRefinement = useRef();
  const appliedSelectionForDrawer = useRef([]);
  const desktopSort = useRef('');

  const isMobile = channel === 'mobile';
  const isBrowsePage = pageContext?.pageType === 'browse';
  const variables = { ...customContext?.variables || {} };
  const isControlledData = typeof wrapperData !== 'undefined' || typeof wrapperLoading !== 'undefined';

  const defaultOptions = {
    variables,
    ssr: true,
    skip: isControlledData || (!variables?.keyword && !variables?.navParam)
  };

  const {
    storefilter,
    isBrandPricingPolicyCompliant = null,
    storeId,
    additionalSearchParams = null,
    loyaltyMembershipInput = null
  } = queryVariables;

  const { data: componentData, loading: componentLoading } = useDataModel('searchModel', defaultOptions);
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const { onDimensionsChangePLP, onSortChangePLP } = ProductResultsSearchMethodsPLP();

  const data = isControlledData ? wrapperData : componentData;
  const loading = isControlledData ? wrapperLoading : componentLoading;

  const [getRefinementCount, { data: refinementCountResponse,
    error: getTotalProductsCountError }] = useLazyDataModel('refinementCountModel');

  const {
    appliedDimensions, taxonomy, dimensions, searchReport, metadata, primaryFilters
  } = data?.searchModel || {};

  const { keyword = null, totalProducts } = searchReport || {};

  const { canonicalUrl = '', clearAllRefinementsURL = '' } = metadata || {};

  const [activeDimension, setActiveDimension] = useState(dimensions?.length > 0 ? dimensions[0] : {});

  let [selectedDimensions, setSelectedDimensions] = useState(
    appliedDimensions ? getAppliedDimensions(appliedDimensions) : []
  );

  const handleSortOrderChange = ({ output: result }) => {
    if (result?.sortby && result?.sortorder) {
      desktopSort.current = `${result.sortby}:${result.sortorder}`;
    }
  };

  useEffect(() => {
    setSelectedDimensions(getAppliedDimensions(appliedDimensions));
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
    let _appliedDimensions = getAppliedDimensions(appliedDimensions);
    if (onDisplay === 'true') {
      setSelectedDimensions([..._appliedDimensions, ON_DISPLAY_ITEM]);
    } else {
      setSelectedDimensions(_appliedDimensions);
    }
  }, [appliedDimensions]);

  const viewProductCountRef = useRef(totalProducts);

  const productCount = useMemo(() => {
    if (getTotalProductsCountError) {
      console.error('Error retrieving refinement count', getTotalProductsCountError);
      return null;
    }

    const refinementProductCount = refinementCountResponse?.refinementCountModel?.searchReport?.totalProducts;
    if (typeof refinementProductCount === 'number') {
      viewProductCountRef.current = refinementProductCount;
    }

    return typeof viewProductCountRef.current === 'number'
      ? viewProductCountRef.current
      : totalProducts;
  }, [refinementCountResponse, getTotalProductsCountError, totalProducts]);

  const isRefinementPresent = () => {
    return selectedDimensions.some((dimension) => dimension?.refinements.some((ref) => ref?.label === ON_DISPLAY_ITEM?.refinements[0]?.label));
  };

  // adding chips into drawer
  const addRefinment = () => {
    if (!isRefinementPresent()) {
      setSelectedDimensions([...selectedDimensions, { ...ON_DISPLAY_ITEM }]);
    }
  };

  // removing chip from drawer
  const removeRefinment = () => {
    if (isRefinementPresent() && selectedDimensions.length) {
      setSelectedDimensions(selectedDimensions.filter((item) => {
        return !item?.refinements.some((refinement) => refinement.refinementLabel === 'On Display');
      }));
    }
  };

  const keepCurrentSelection = () => {
    return getAppliedDimensions(selectedDimensions);
  };

  useEffect(() => {
    setOnDisplayClick(isRefinementPresent());
    appliedSelectionForDrawer.current = keepCurrentSelection();
  }, [selectedDimensions]);

  const onDisplayData = {
    onDisplay,
    onDisplayChange,
    isStoreDisplay: data?.searchModel?.metadata?.isStoreDisplay || null,
    itemCount: data?.searchModel?.metadata?.productCount?.inStore
  };

  const onDimensionsChangeInternal = useCallback(({ refinement }) => {
    return onDimensionsChangePLP({
      refinement,
      viewPrimaryFilters,
    });
  }, [viewPrimaryFilters]);

  const onSortChangeInternal = useCallback(
    ({
      sortby, sortorder, name, currentPage, page, updatedPath = null
    }) => {
      return onSortChangePLP({
        sortby,
        sortorder,
        name,
        currentPage,
        page,
        updatedPath,
        path,
      });
    }, [path]
  );

  const extractBaseNavParam = useCallback(() => {
    const match = clearAllRefinementsURL.match(/(N-[^/?]+)/);
    return match ? match[1] : '';
  }, [clearAllRefinementsURL]);

  // Use prop functions if provided, otherwise use internal functions
  const onDimensionsChange = onDimensionsChangeProp || onDimensionsChangeInternal;
  const onSortChange = onSortChangeProp || onSortChangeInternal;

  if (!viewPrimaryFilters) {
    return null;
  }
  if (!dimensions) {
    return <FilterAndSortPlaceholder numOf={5} />;
  }
  if (dimensions.length === 0) {
    return null;
  }

  const hideViewAllInternal = !viewPrimaryFilters;
  const hideViewAll = hideViewAllProp !== undefined ? hideViewAllProp : hideViewAllInternal;

  const { breadCrumbs = [] } = taxonomy || {};

  const handleSecondaryFilterDrawerToggle = (isOpen) => {
    if (isOpen) {
      setSelectedDimensions(appliedSelectionForDrawer.current);
    } else {
      appliedSelectionForDrawer.current = keepCurrentSelection();
    }
    setDrawerOpen(!drawerOpen);
    publish('change-secondary-filter', !drawerOpen);
  };

  const handlePrimaryDrawerToggle = (isOpen, dimension, selectedRefinements, name) => {
    setPrimaryFilterDrawerOpen(isOpen);
    setActiveDimension(dimension);

    if (isOpen) {
      setSelectedDimensions(appliedSelectionForDrawer.current);
    } else {
      appliedSelectionForDrawer.current = keepCurrentSelection();
    }

    // close performed, keep all previous selections
    // apply performed, keep only applied selections, remove other selections
    const prevSelections = appliedSelectionForDrawer.current
      .filter((item) => item.label !== dimension.label);
    const newDims = selectedRefinements?.length
      ? createDimensionWithRefinements(dimension.label, selectedRefinements) : [];

    appliedSelectionForDrawer.current = name === 'close'
      ? [...prevSelections, ...newDims]
      : keepCurrentSelection();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const ensureNavParamPrefix = (navParam) => (navParam.startsWith('N-') ? navParam : `N-${navParam}`);

  const collectRefinementKeys = (selectedRefinements = []) => selectedRefinements.flatMap(
    (group) => (group?.refinements || [])
      .filter((ref) => ref?.refinementKey)
      .map((ref) => ref.refinementKey)
  );

  const extractCustomPriceFilter = (selectedRefinements = []) => {
    const priceRef = selectedRefinements
      .flatMap((group) => group?.refinements || [])
      .find((ref) => ref?.lowerbound && ref?.upperbound);

    return priceRef
      ? {
        rangefilter: 'price',
        lowerBound: Number(priceRef.lowerbound),
        upperBound: Number(priceRef.upperbound)
      }
      : null;
  };

  const getNavRootForApply = (baseNavParam) => {
    if (isBrowsePage) return baseNavParam || '';
    if (baseNavParam.includes('Z')) {
      return extractBaseNavParam(); // strip previously appended keys
    }
    const firstZ = baseNavParam.indexOf('Z');
    const secondZ = baseNavParam.indexOf('Z', firstZ + 1);
    if (firstZ !== -1 && secondZ !== -1) {
      return baseNavParam.slice(0, secondZ);
    }
    return baseNavParam;
  };

  const getNavParamForClearAll = (baseNavParam) => {
    if (isBrowsePage) return extractBaseNavParam();
    const firstZ = baseNavParam.indexOf('Z');
    if (firstZ === -1) return extractBaseNavParam();
    // remove everything after first refinement marker
    return extractBaseNavParam().substring(0, firstZ);
  };

  const buildNavParam = (baseNavParam, selectedRefinements) => {
    if (!selectedRefinements.length) {
      return getNavParamForClearAll(baseNavParam);
    }
    const root = getNavRootForApply(baseNavParam);
    const keys = collectRefinementKeys(selectedRefinements);
    return keys.length ? `${root}Z${keys.join('Z')}` : root;
  };

  const executeGetTotalProductsCount = (updatedSelectedRefinements = []) => {
    try {
      const baseNavParam = extractNavParamFromClearAllURL(wrapperData) || '';
      const navParamRaw = buildNavParam(baseNavParam, updatedSelectedRefinements);
      const customPriceFilter = extractCustomPriceFilter(updatedSelectedRefinements);
      const productVariables = {
        navParam: ensureNavParamPrefix(navParamRaw),
        keyword,
        storefilter,
        isBrandPricingPolicyCompliant,
        storeId,
        channel: channel.toUpperCase(),
        additionalSearchParams,
        loyaltyMembershipInput,
        filter: customPriceFilter
      };

      getRefinementCount({ variables: productVariables });
    } catch (err) {
      console.error('executeGetTotalProductsCount failed', err);
    }
  };

  // handle primary filter drawer toggle
  // builds url and call dimension changed
  /* istanbul ignore next */
  const applyFilters = () => {
    document.body.classList.add('sui-opacity-50');
    setTimeout(() => {
      if (clearAllRefinement.current !== undefined) {
        appliedSelectionForDrawer.current = [];
        onDimensionsChange({ refinement: clearAllRefinement.current });
        closeDrawer();
        document.body.classList.remove('sui-opacity-50');
        return;
      }
      let sortedSelectedDimensions = sortSelectedDimensions(selectedDimensions);
      if (selectedDimensions && Object.keys(selectedDimensions).length > 0) {
        storeLifecycleEvent('product-results.change-filters-refinements-secondary', selectedDimensions);
      }
      // get refinement keys and sort them by id
      let refinementKeys = sortedSelectedDimensions
        .map((dim) => dim.refinements.map((ref) => ref.refinementKey)).flat(1);

      let sortedRefinementKeys = sortRefinementKeys(refinementKeys);

      // get dimensions that may go on the url
      let dimensionsIncludedInUrl = getDimensionsForUrl(sortedSelectedDimensions);

      // get refinements from the previous dimension list
      let refinementsIncludedInUrl = dimensionsIncludedInUrl?.map(
        (_dimension) => _dimension.refinements.sort(sortAscending)).flat(1);
      let length = refinementsIncludedInUrl && refinementsIncludedInUrl.length;
      let lastRefinement = refinementsIncludedInUrl && refinementsIncludedInUrl[length - 1];
      let urlParts = clearAllRefinementsURL?.split('?')[0].split('/');

      // get custom price range
      const priceDimension = selectedDimensions.find((_dimension) => _dimension.label === 'Price');
      const [lowerbound, upperbound] = findCustomPriceRange(priceDimension?.refinements);
      const canonicalUrlParts = canonicalUrl.split('?');
      let queryParams = canonicalUrlParts.length > 1 ? canonicalUrlParts[1] : '';
      if (lowerbound || upperbound) {
        let queries = queryParams.split('&')
          .filter((query) => !query.toLowerCase().includes('lowerbound') && !query.toLowerCase().includes('upperbound'));
        queryParams = queries.join('&');
        queryParams += `&lowerbound=${lowerbound}&upperbound=${upperbound}`;
      }

      queryParams = filtersCatStyleCheck(queryParams);

      queryParams = sortOrder({ desktopSort, queryParams });

      const nValue = urlParts.find((part) => part.startsWith('N-')) || '';
      let refinementsIds = nValue;
      if (sortedRefinementKeys.length > 0) {
        refinementsIds += 'Z' + sortedRefinementKeys.join('Z');
      }
      // get refinement labels for url
      const refinementsLablesForUrl = getRefinemntsLabelsForUrl(refinementsIncludedInUrl);
      urlParts.splice(urlParts.indexOf(nValue), 0, ...refinementsLablesForUrl);

      urlParts.splice(urlParts.indexOf(nValue), 1, refinementsIds);
      lastRefinement = { ...lastRefinement, url: urlParts.join('/') + '?' + queryParams };
      // appliedSelectionForDrawer.current = [];
      // pass dimension
      if (lastRefinement?.refinementKey === 'onDisplay') {
        onDimensionsChange({ refinement: lastRefinement, onDisplay: true });
      } else {
        onDimensionsChange({ refinement: lastRefinement });
      }
      handleSecondaryFilterDrawerToggle(false);
      closeDrawer();
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  // update selected refinements list
  const updateSelectedRefinements = ({ refinement, dimension, radio, multiRefinements }) => {
    clearAllRefinement.current = undefined;
    let updatedDimensions = selectedDimensions.filter((dim) => dim.label !== 'Clear');
    let index = selectedDimensions.findIndex((dim) => {
      return dim.label === dimension.label;
    });

    if (multiRefinements) {
      updatedDimensions = [
        ...selectedDimensions,
        {
          label: dimension.label,
          refinements: [...multiRefinements]
        }
      ];
    }

    if (radio && refinement === undefined) {
      updatedDimensions = selectedDimensions.filter((item) => item.label !== 'Review Rating');
    } else if (refinement && index >= 0) {
      updatedDimensions = selectedDimensions.slice();
      let selectedRefinement = -1;
      if (dimension.dimensionId === 'custom_price') {
        let priceRangeList = updatedDimensions[index].refinements.filter((ref) => ref.refinementKey !== refinement.refinementKey);
        updatedDimensions[index].refinements = priceRangeList;
      }
      if (updatedDimensions.length > 0) {
        selectedRefinement = updatedDimensions[index].refinements.findIndex(
          (ref) => ref.refinementKey === refinement.refinementKey);
      }
      if (radio && selectedRefinement === -1) {
        updatedDimensions[index].refinements = [];
      }
      if (selectedRefinement >= 0) {
        updatedDimensions[index].refinements = updatedDimensions[index].refinements
          .filter((_refinement) => (_refinement.refinementKey !== refinement.refinementKey) || radio);
        if (updatedDimensions[index].refinements.length === 0) {
          updatedDimensions.splice(index, 1);
        }
      } else {
        updatedDimensions[index].refinements = updatedDimensions[index].refinements.slice();
        updatedDimensions[index].refinements.push(refinement);
      }
    } else if (refinement && refinement.clear) {
      return [];
    } else if (refinement) {
      updatedDimensions = selectedDimensions.slice();
      updatedDimensions.push({
        label: dimension.label,
        refinements: [refinement]
      });
    }
    return updatedDimensions;
  };

  const selectRefinement = ({
    refinement, dimension, radio, multiRefinements, isChecked
  }) => {
    if (isChecked !== undefined
      && (refinement?.refinementKey === 'onDisplay' || refinement?.label === 'Pick Up Today')) {
      if (isChecked) {
        addRefinment();
      } else {
        removeRefinment();
      }
      return;
    }
    const updatedSelectedRefinements = updateSelectedRefinements({ refinement, dimension, radio, multiRefinements });
    setSelectedDimensions(updatedSelectedRefinements);
    executeGetTotalProductsCount(updatedSelectedRefinements);
  };

  const handleClearAll = () => {
    const url = getClearAllWithSort({ metadata, searchReport });
    clearAllRefinement.current = {
      url,
      clear: true
    };
    removeRefinment();
    setOnDisplayClick(false);
    setSelectedDimensions([]);
    executeGetTotalProductsCount([]);
  };

  ResultsFilterAndSortDrawer.handlePrimaryDrawerToggle = handlePrimaryDrawerToggle;
  const sortedDimensions = sortDimensions({ dimensions, searchReport, metadata });

  const onRefinementChange = ({ refinement, dimension }) => {
    setPrimaryFilterDrawerOpen(false);
    document.body.classList.add('sui-opacity-50');
    /* istanbul ignore next */
    setTimeout(() => {
      if (onDimensionsChange && dimension) {
        const updatedSelectedRefinements = updateSelectedRefinements({ refinement, dimension });
        setSelectedDimensions(updatedSelectedRefinements);
        const refinementUrl = getRefinementUrlWithSort({ dimension, metadata, refinement, searchReport });
        const { label: dimensionName } = dimension;
        const updatedRefinement = {
          ...refinement,
          url: refinementUrl,
          dimensionName
        };
        onDimensionsChange({ refinement: updatedRefinement });
      } else if (onDimensionsChange && !dimension) {
        onDimensionsChange({ refinement });
      }
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  return (
    <OnDisplayContext.Provider value={{ onDisplayClick, setOnDisplayClick }}>
      <div className="sui-overflow-x-auto" data-component="ResultsFilterAndSortDrawer">
        <Drawer
          appliedDimensions={selectedDimensions}
          breadCrumbs={breadCrumbs}
          isOpen={drawerOpen}
          onSortByChange={onSortChange}
          onRefinementChange={onRefinementChange}
          onRefinementSelected={onRefinementSelected || selectRefinement}
          metadata={metadata}
          normalizedDimensions={sortedDimensions}
          searchReport={searchReport}
          toggleDrawer={handleSecondaryFilterDrawerToggle}
          onDisplayData={onDisplayData}
          loading={loading}
          enableMultiStore={enableMultiStore}
          onMultiStoreSelectionChange={onMultiStoreSelectionChange}
          multiStoreSelection={multiStoreSelection}
          onDimensionsChange={applyFilters}
          clearAll={handleClearAll}
          isTableView={isTableView}
          productCount={productCount}
        />
        {!disableStickyFilter ? (
          <div className="sui-h-14">
            <Sticky
              animation="slide"
              className="sui-w-full sui-z-100"
              offsetMargin={stickyOffset}
              position="top"
              stickyType="showOnScrollUp"
              visibleByDefault
            >
              <PrimaryFilters
                activeDimension={activeDimension}
                appliedDimensions={selectedDimensions}
                dimensions={dimensions}
                primaryFilters={primaryFilters}
                isMobile={isMobile}
                isOpen={primaryFilterDrawerOpen}
                onRefinementChange={onRefinementChange}
                onCloseDrawer={handlePrimaryDrawerToggle}
                onSecondaryFilterDrawerToggle={handleSecondaryFilterDrawerToggle}
                canonicalUrl={canonicalUrl}
                baseUrl={clearAllRefinementsURL}
                hideViewAll={hideViewAll}
                desktopSort={desktopSort}
              />
            </Sticky>
          </div>
        ) : (
          <PrimaryFilters
            activeDimension={activeDimension}
            appliedDimensions={selectedDimensions}
            dimensions={dimensions}
            primaryFilters={primaryFilters}
            isMobile={isMobile}
            isOpen={primaryFilterDrawerOpen}
            onCloseDrawer={handlePrimaryDrawerToggle}
            onRefinementChange={onRefinementChange}
            onSecondaryFilterDrawerToggle={handleSecondaryFilterDrawerToggle}
            canonicalUrl={canonicalUrl}
            baseUrl={clearAllRefinementsURL}
            hideViewAll={hideViewAll}
            desktopSort={desktopSort}
          />
        )}
        {isMobile && !isGetItFastOnPlp && (
          <MainFilter
            dimensions={sortedDimensions}
            enableMultiStore={enableMultiStore}
            multiStoreSelection={multiStoreSelection}
            onChange={onDisplay === 'true' ? onDisplayChange : onRefinementChange}
            onDisplayData={onDisplayData}
            onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            isPrimaryFilters
          />
        )}
      </div>
    </OnDisplayContext.Provider>
  );
};

ResultsFilterAndSortDrawer.propTypes = {
  onDimensionsChange: func,
  onRefinementSelected: func,
  onSortChange: func,
  stickyOffset: number,
  enableMultiStore: bool,
  multiStoreSelection: string,
  onDisplay: string,
  onDisplayChange: func,
  disableStickyFilter: bool,
  isTableView: bool,
  hideViewAll: bool,
  isGetItFastOnPlp: bool
};

ResultsFilterAndSortDrawer.defaultProps = {
  onDimensionsChange: null,
  onRefinementSelected: null,
  onSortChange: null,
  stickyOffset: 0,
  enableMultiStore: false,
  multiStoreSelection: null,
  onDisplay: 'false',
  onDisplayChange: null,
  disableStickyFilter: false,
  isTableView: false,
  hideViewAll: false,
  isGetItFastOnPlp: false
};

ResultsFilterAndSortDrawer.displayName = 'ResultsFilterAndSortDrawer';

ResultsFilterAndSortDrawer.dataModel = extend(MainFilter, Drawer, productRefinementCountDataModel,
  productResultsDataModel);

export { ResultsFilterAndSortDrawer };
